import type { AxiosInstance } from "axios";

import { parseAxiosResponse } from "@thalamos/common";
import type {
  AdministeredOrganisation,
  CreateOrganisationRequest,
  Organisation,
  OrganisationDirectoryPage,
  OrganisationId,
  OrganisationRelationshipId,
  OrganisationRelationships,
  OrganisationStructure,
  OrganisationType,
  OrganisationUserMemberships,
  RelateOrganisationRequest,
  RenameOrganisationRequest,
  SetOrganisationTypeRequest,
  SetReadsApprovedAssessmentsThroughMembersRequest,
  UserDirectoryPage,
} from "@thalamos/haven-common";
import {
  AdministeredOrganisationSchema,
  OrganisationDirectoryPageSchema,
  OrganisationRelationshipsSchema,
  OrganisationSchema,
  OrganisationStructureSchema,
  OrganisationUserMembershipsSchema,
  UserDirectoryPageSchema,
} from "@thalamos/haven-common";

import type { ErrorBody, ResponseBody } from "../axios/api.js";
import { useApiAxios } from "../axios/api.js";

const parseResponseUserPage = parseAxiosResponse(UserDirectoryPageSchema);
const parseResponseOrganisationPage = parseAxiosResponse(
  OrganisationDirectoryPageSchema,
);
const parseResponseAdministeredOrganisation = parseAxiosResponse(
  AdministeredOrganisationSchema,
);
const parseResponseOrganisation = parseAxiosResponse(OrganisationSchema);
const parseResponseRelationships = parseAxiosResponse(
  OrganisationRelationshipsSchema,
);
const parseResponseStructure = parseAxiosResponse(OrganisationStructureSchema);
const parseResponseMemberships = parseAxiosResponse(
  OrganisationUserMembershipsSchema,
);

/**
 * A created organisation answers 201, which `parseAxiosResponse` leaves alone,
 * so the body is parsed here when it arrives. A refusal keeps its error body.
 */
const parseCreated = (response: {
  status: number;
  data: unknown;
}): ResponseBody<Organisation, ErrorBody> =>
  response.status === 201
    ? OrganisationSchema.parse(response.data)
    : (response.data as ErrorBody);

/**
 * A page of a listing, as a caller asks for it.
 *
 * `query` is optional and an absent one lists everything — which is what an
 * administrative directory is for, and what no customer-facing search may do.
 */
export type DirectoryQuery = {
  query?: string;
  limit?: number;
  offset?: number;
};

/**
 * Only the parameters a caller actually named are sent, so the server's defaults
 * stay the single definition of a page size rather than being restated here and
 * drifting from it.
 */
export const directoryParams = ({ query, limit, offset }: DirectoryQuery) => ({
  ...(query !== undefined && query.length > 0 ? { query } : {}),
  ...(limit !== undefined ? { limit } : {}),
  ...(offset !== undefined ? { offset } : {}),
});

/** A page of organisations, narrowed by kind as well; no kind lists every kind. */
export type OrganisationDirectoryQuery = DirectoryQuery & {
  type?: readonly OrganisationType[];
};

const organisationDirectoryParams = ({
  type,
  ...request
}: OrganisationDirectoryQuery) => ({
  ...directoryParams(request),
  ...(type !== undefined && type.length > 0 ? { type } : {}),
});

export const config = {
  admin: {
    users: (request: DirectoryQuery = {}) => ({
      url: "/api/admin/users",
      method: "get",
      params: directoryParams(request),
      transformResponse: parseResponseUserPage,
    }),
    organisations: (request: OrganisationDirectoryQuery = {}) => ({
      url: "/api/admin/organisations",
      method: "get",
      params: organisationDirectoryParams(request),
      transformResponse: parseResponseOrganisationPage,
    }),
    organisation: (organisationId: string) => ({
      url: `/api/admin/organisations/${organisationId}`,
      method: "get",
      transformResponse: parseResponseAdministeredOrganisation,
    }),
    organisationRelationships: (organisationId: string) => ({
      url: `/api/admin/organisations/${organisationId}/relationships`,
      method: "get",
      transformResponse: parseResponseRelationships,
    }),
    organisationStructure: (organisationId: string) => ({
      url: `/api/admin/organisations/${organisationId}/structure`,
      method: "get",
      transformResponse: parseResponseStructure,
    }),
    organisationUserMemberships: (organisationId: string) => ({
      url: `/api/admin/organisations/${organisationId}/user-memberships`,
      method: "get",
      transformResponse: parseResponseMemberships,
    }),
    createOrganisation: (body: CreateOrganisationRequest) => ({
      url: "/api/admin/organisations",
      method: "post",
      data: body,
    }),
    setReadsApprovedAssessmentsThroughMembers: (
      organisationId: OrganisationId,
      body: SetReadsApprovedAssessmentsThroughMembersRequest,
    ) => ({
      url: `/api/admin/organisations/${organisationId}/reads-approved-assessments-through-members`,
      method: "put",
      data: body,
      transformResponse: parseResponseAdministeredOrganisation,
    }),
    renameOrganisation: (
      organisationId: OrganisationId,
      body: RenameOrganisationRequest,
    ) => ({
      url: `/api/admin/organisations/${organisationId}/name`,
      method: "put",
      data: body,
      transformResponse: parseResponseOrganisation,
    }),
    setOrganisationType: (
      organisationId: OrganisationId,
      body: SetOrganisationTypeRequest,
    ) => ({
      url: `/api/admin/organisations/${organisationId}`,
      method: "patch",
      data: body,
      transformResponse: parseResponseOrganisation,
    }),
    relateOrganisation: (
      organisationId: OrganisationId,
      body: RelateOrganisationRequest,
    ) => ({
      url: `/api/admin/organisations/${organisationId}/relationships`,
      method: "post",
      data: body,
    }),
    unrelateOrganisation: (
      organisationId: OrganisationId,
      relationshipId: OrganisationRelationshipId,
    ) => ({
      url: `/api/admin/organisations/${organisationId}/relationships/${relationshipId}`,
      method: "delete",
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => {
  return {
    admin: {
      users: (request: DirectoryQuery = {}) =>
        apiAxios.request<ResponseBody<UserDirectoryPage, ErrorBody>>(
          config.admin.users(request),
        ),
      organisations: (request: OrganisationDirectoryQuery = {}) =>
        apiAxios.request<ResponseBody<OrganisationDirectoryPage, ErrorBody>>(
          config.admin.organisations(request),
        ),
      createOrganisation: async (body: CreateOrganisationRequest) => {
        const response = await apiAxios.request<unknown>(
          config.admin.createOrganisation(body),
        );

        return { status: response.status, data: parseCreated(response) };
      },
      setReadsApprovedAssessmentsThroughMembers: (
        organisationId: OrganisationId,
        body: SetReadsApprovedAssessmentsThroughMembersRequest,
      ) =>
        apiAxios.request<ResponseBody<AdministeredOrganisation, ErrorBody>>(
          config.admin.setReadsApprovedAssessmentsThroughMembers(
            organisationId,
            body,
          ),
        ),
      renameOrganisation: (
        organisationId: OrganisationId,
        body: RenameOrganisationRequest,
      ) =>
        apiAxios.request<ResponseBody<Organisation, ErrorBody>>(
          config.admin.renameOrganisation(organisationId, body),
        ),
      setOrganisationType: (
        organisationId: OrganisationId,
        body: SetOrganisationTypeRequest,
      ) =>
        apiAxios.request<ResponseBody<Organisation, ErrorBody>>(
          config.admin.setOrganisationType(organisationId, body),
        ),
      // The success bodies of these two are not modelled: only the status and a
      // refusal's reason are read.
      relateOrganisation: (
        organisationId: OrganisationId,
        body: RelateOrganisationRequest,
      ) =>
        apiAxios.request<ErrorBody>(
          config.admin.relateOrganisation(organisationId, body),
        ),
      unrelateOrganisation: (
        organisationId: OrganisationId,
        relationshipId: OrganisationRelationshipId,
      ) =>
        apiAxios.request<ErrorBody>(
          config.admin.unrelateOrganisation(organisationId, relationshipId),
        ),
    },
  };
};

export const apiHooks = () => {
  return {
    admin: {
      users: function useAdminUsers(
        request: DirectoryQuery = {},
        options?: Parameters<typeof useApiAxios<UserDirectoryPage>>[1],
      ) {
        return useApiAxios<UserDirectoryPage>(
          config.admin.users(request),
          options,
        );
      },
      organisations: function useAdminOrganisations(
        request: OrganisationDirectoryQuery = {},
        options?: Parameters<typeof useApiAxios<OrganisationDirectoryPage>>[1],
      ) {
        return useApiAxios<OrganisationDirectoryPage>(
          config.admin.organisations(request),
          options,
        );
      },
      organisation: function useAdminOrganisation(organisationId: string) {
        return useApiAxios<AdministeredOrganisation>(
          config.admin.organisation(organisationId),
        );
      },
      organisationRelationships: function useAdminOrganisationRelationships(
        organisationId: string,
      ) {
        return useApiAxios<OrganisationRelationships>(
          config.admin.organisationRelationships(organisationId),
        );
      },
      organisationStructure: function useAdminOrganisationStructure(
        organisationId: string,
        options?: Parameters<typeof useApiAxios<OrganisationStructure>>[1],
      ) {
        return useApiAxios<OrganisationStructure>(
          config.admin.organisationStructure(organisationId),
          options,
        );
      },
      organisationUserMemberships: function useAdminOrganisationUserMemberships(
        organisationId: string,
      ) {
        return useApiAxios<OrganisationUserMemberships>(
          config.admin.organisationUserMemberships(organisationId),
        );
      },
    },
  };
};
